import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, ChevronDown, AlertCircle, CheckCircle2, FileText, Package, FileDown, MoreHorizontal, Link2, RotateCcw, Clock3 } from 'lucide-react';
import { Button } from '../../ui/button';
import { EmailComposer } from '../EmailComposer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STAGES = [
  { id: 'lead', label: 'Lead', color: 'bg-blue-500' },
  { id: 'discovery', label: 'Discovery', color: 'bg-yellow-500' },
  { id: 'pre_assessment', label: 'Pre-Assessment', color: 'bg-orange-400' },
  { id: 'docs_requested', label: 'Docs Requested', color: 'bg-amber-500' },
  { id: 'application_ready', label: 'Application Ready', color: 'bg-lime-500' },
  { id: 'submitted_to_lender', label: 'Submitted', color: 'bg-purple-500' },
  { id: 'conditional_approval', label: 'Conditional', color: 'bg-green-500' },
  { id: 'conditions_outstanding', label: 'Conditions Outstanding', color: 'bg-cyan-500' },
  { id: 'final_approval', label: 'Final Approval', color: 'bg-emerald-500' },
  { id: 'settlement_booked', label: 'Settlement Booked', color: 'bg-teal-500' },
  { id: 'settled', label: 'Settled', color: 'bg-emerald-600' },
  { id: 'post_settlement', label: 'Post-Settlement', color: 'bg-green-700' },
  { id: 'declined', label: 'Declined', color: 'bg-red-500' },
  { id: 'lost', label: 'Lost', color: 'bg-gray-500' },
  // legacy
  { id: 'contacted', label: 'Contacted', color: 'bg-yellow-500' },
  { id: 'application', label: 'Application', color: 'bg-orange-500' },
  { id: 'submitted', label: 'Submitted', color: 'bg-purple-500' },
  { id: 'approved', label: 'Approved', color: 'bg-green-500' },
  { id: 'docs_out', label: 'Docs Out', color: 'bg-teal-500' },
];

export default function WorkspaceHeader({ deal, completeness, onRefresh }) {
  const navigate = useNavigate();
  const [showStages, setShowStages] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [portalStatus, setPortalStatus] = useState(null);
  const [sendingLink, setSendingLink] = useState(false);
  const [showPortalEmailComposer, setShowPortalEmailComposer] = useState(false);
  const [portalEmailTemplate, setPortalEmailTemplate] = useState(null);

  const currentStage = STAGES.find(s => s.id === deal.stage) || STAGES[0];
  const overall = completeness?.overall || 0;
  const docPct = completeness?.document_completeness || 0;

  const fetchPortalStatus = async () => {
    try {
      const res = await axios.get(`${API}/crm/deals/${deal.id}/portal/status`);
      setPortalStatus(res.data);
    } catch (error) {
      setPortalStatus(null);
    }
  };

  useEffect(() => {
    if (deal?.id) fetchPortalStatus();
  }, [deal?.id]);

  const handleTransition = async (targetStage) => {
    setTransitioning(true);
    try {
      await axios.post(`${API}/crm/workflow/transition`, {
        deal_id: deal.id, target_stage: targetStage, force: true
      });
      toast.success(`Stage updated to ${STAGES.find(s => s.id === targetStage)?.label || targetStage}`);
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change stage');
    } finally {
      setTransitioning(false);
      setShowStages(false);
    }
  };

  const handleDownload = async (endpoint, filename, successMessage, key) => {
    setDownloading(key);
    try {
      const res = await axios.post(endpoint, {}, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(successMessage);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(null);
    }
  };

  const buildPortalEmailTemplate = (portalLink) => ({
    to: deal.client_email || portalStatus?.contact_email || '',
    subject: `Complete your finance application - ${deal.client_name || 'FlashIQ Client'}`,
    body: `Hi ${deal.client_name || ''},

Please complete your finance application using the secure link below:

${portalLink}

This link is secure, expires in 14 days, and can be resumed anytime before final submission.

If you have questions, just reply to this email.

Thanks,
FlashIQ Team`,
  });

  const handleSendApplicationLink = async () => {
    setSendingLink(true);
    try {
      const res = await axios.post(`${API}/crm/deals/${deal.id}/portal/link`);
      const portalLink = res.data.portal_link;
      setPortalStatus(res.data);
      setPortalEmailTemplate(buildPortalEmailTemplate(portalLink));
      setShowPortalEmailComposer(true);
      toast.success('Application link generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate application link');
    } finally {
      setSendingLink(false);
    }
  };

  const handleCopyPortalLink = async () => {
    try {
      const res = await axios.post(`${API}/crm/deals/${deal.id}/portal/link`);
      await navigator.clipboard.writeText(res.data.portal_link);
      setPortalStatus(res.data);
      toast.success('Application link copied');
    } catch (error) {
      toast.error('Failed to copy link');
    }
  };

  const handleReopenPortal = async () => {
    try {
      const res = await axios.post(`${API}/crm/deals/${deal.id}/portal/reopen`);
      setPortalStatus(res.data);
      toast.success('Portal reopened for client updates');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen portal');
    }
  };

  const portalStatusLabel = (portalStatus?.status || 'not_started').replace(/_/g, ' ');
  const portalBadgeClass = portalStatus?.status === 'submitted'
    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/30'
    : portalStatus?.status === 'in_progress'
      ? 'bg-blue-500/15 text-blue-300 border-blue-400/30'
      : 'bg-gray-500/15 text-gray-300 border-gray-400/30';

  return (
    <div className="bg-navy-950 border-b border-white/10 px-6 py-4" data-testid="workspace-header">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/crm/pipeline')} className="text-gray-400 hover:text-white" data-testid="back-to-pipeline">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-semibold text-white" data-testid="deal-client-name">{deal.client_name}</h1>
            <p className="text-sm text-gray-400">{deal.business_name || 'No business'} &middot; ${(deal.amount || 0).toLocaleString()} {deal.finance_type}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Health indicators */}
          <div className="hidden md:flex items-center gap-3 text-xs">
            <HealthPill label="Readiness" value={overall} />
            <HealthPill label="Docs" value={docPct} />
            {completeness?.counts?.tasks_overdue > 0 && (
              <span className="flex items-center gap-1 text-red-400">
                <AlertCircle className="h-3.5 w-3.5" />{completeness.counts.tasks_overdue} overdue
              </span>
            )}
          </div>
          {/* Stage dropdown */}
          <div className="relative">
            <button onClick={() => setShowStages(!showStages)} disabled={transitioning}
              data-testid="stage-dropdown-trigger"
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium text-white ${currentStage.color}`}>
              {currentStage.label} <ChevronDown className="h-3 w-3" />
            </button>
            {showStages && (
              <div className="absolute right-0 top-full mt-1 w-52 bg-navy-800 border border-white/10 rounded-lg shadow-xl z-50 py-1 max-h-72 overflow-y-auto"
                data-testid="stage-dropdown-menu">
                {STAGES.filter(s => !['contacted','application','submitted','approved','docs_out'].includes(s.id)).map(s => (
                  <button key={s.id} onClick={() => handleTransition(s.id)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex items-center gap-2 ${s.id === deal.stage ? 'text-purple-400' : 'text-gray-300'}`}>
                    <span className={`w-2 h-2 rounded-full ${s.color}`} />{s.label}
                    {s.id === deal.stage && <CheckCircle2 className="h-3 w-3 ml-auto" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className={`hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs capitalize ${portalBadgeClass}`} data-testid="portal-status-pill">
            <Clock3 className="h-3.5 w-3.5" /> Portal: <span data-testid="portal-status-text">{portalStatusLabel}</span>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="border-white/10 text-gray-300 hover:text-white hover:bg-white/5"
                data-testid="workspace-actions-trigger"
              >
                <MoreHorizontal className="h-4 w-4 mr-1.5" /> Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 bg-navy-900 border-white/10" data-testid="workspace-actions-menu">
              <DropdownMenuLabel className="text-gray-300" data-testid="workspace-actions-label">Exports</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleDownload(
                    `${API}/crm/deals/${deal.id}/application-pdf`,
                    `application-${deal.id.slice(0, 8)}.pdf`,
                    'Application PDF downloaded',
                    'application_pdf'
                  );
                }}
                className="text-gray-200 focus:bg-white/10 focus:text-white"
                data-testid="download-application-pdf-action"
              >
                <FileDown className="h-4 w-4" />
                <span>{downloading === 'application_pdf' ? 'Generating Application PDF...' : 'Download Application PDF'}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleDownload(
                    `${API}/crm/deals/${deal.id}/submission-pack/pdf`,
                    `deal-summary-${deal.id.slice(0, 8)}.pdf`,
                    'Submission summary PDF downloaded',
                    'submission_pdf'
                  );
                }}
                className="text-gray-200 focus:bg-white/10 focus:text-white"
                data-testid="download-summary-pdf-action"
              >
                <FileText className="h-4 w-4" />
                <span>{downloading === 'submission_pdf' ? 'Generating Summary PDF...' : 'Download Submission Summary PDF'}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleDownload(
                    `${API}/crm/deals/${deal.id}/submission-pack/zip`,
                    `submission-pack-${deal.id.slice(0, 8)}.zip`,
                    'Submission ZIP downloaded',
                    'submission_zip'
                  );
                }}
                className="text-gray-200 focus:bg-white/10 focus:text-white"
                data-testid="download-submission-zip-action"
              >
                <Package className="h-4 w-4" />
                <span>{downloading === 'submission_zip' ? 'Generating ZIP...' : 'Download Submission Pack ZIP'}</span>
              </DropdownMenuItem>

              <DropdownMenuSeparator className="bg-white/10" />
              <DropdownMenuLabel className="text-gray-300" data-testid="workspace-actions-portal-label">Client Portal</DropdownMenuLabel>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleSendApplicationLink();
                }}
                className="text-gray-200 focus:bg-white/10 focus:text-white"
                data-testid="send-application-link-action"
              >
                <FileDown className="h-4 w-4" />
                <span>{sendingLink ? 'Generating Link...' : 'Send Application Link'}</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleCopyPortalLink();
                }}
                className="text-gray-200 focus:bg-white/10 focus:text-white"
                data-testid="copy-application-link-action"
              >
                <Link2 className="h-4 w-4" />
                <span>Copy Application Link</span>
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  handleReopenPortal();
                }}
                className="text-gray-200 focus:bg-white/10 focus:text-white"
                data-testid="reopen-application-link-action"
              >
                <RotateCcw className="h-4 w-4" />
                <span>Reopen Portal</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {portalStatus?.last_client_activity_at && (
        <div className="mt-2 text-xs text-gray-400" data-testid="portal-last-activity-text">
          Last client activity: {new Date(portalStatus.last_client_activity_at).toLocaleString()}
        </div>
      )}

      {showPortalEmailComposer && (
        <EmailComposer
          deal={deal}
          onClose={() => setShowPortalEmailComposer(false)}
          onSent={() => fetchPortalStatus()}
          prefillTemplate={portalEmailTemplate}
        />
      )}
    </div>
  );
}

function HealthPill({ label, value }) {
  const color = value >= 75 ? 'text-green-400' : value >= 40 ? 'text-yellow-400' : 'text-red-400';
  return (
    <span className="flex items-center gap-1.5 bg-white/5 px-2.5 py-1 rounded-full">
      <span className="text-gray-400">{label}</span>
      <span className={`font-semibold ${color}`}>{value}%</span>
    </span>
  );
}
